import React from 'react';
import { Outlet, Link as RouterLink, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Container,
  Typography,
  Button,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Badge,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import { palette } from '../../theme/colors';
import { authService, tokenManager } from '../../services/authService';
import { b2bService, PortalInfo, AccessStatusResponse } from '../../services/b2bService';

// Export for child components
export interface B2BPortalContext {
  portalInfo: PortalInfo;
  accessStatus: AccessStatusResponse | null;
  isAuthenticated: boolean;
  user: ReturnType<typeof tokenManager.getUser>;
}

export const B2BPortalLayout: React.FC = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [cartItemCount, setCartItemCount] = React.useState(0);

  // Use unified authentication
  const isAuthenticated = authService.isAuthenticated();
  const user = tokenManager.getUser();

  // Track cart items count
  React.useEffect(() => {
    const updateCartCount = () => {
      const cartStr = localStorage.getItem('b2b_cart');
      if (cartStr) {
        try {
          const cart = JSON.parse(cartStr);
          const count = cart.reduce((sum: number, item: { quantity: number }) => sum + item.quantity, 0);
          setCartItemCount(count);
        } catch {
          setCartItemCount(0);
        }
      } else {
        setCartItemCount(0);
      }
    };

    // Initial load
    updateCartCount();

    // Listen for storage changes (including from other tabs)
    window.addEventListener('storage', updateCartCount);

    // Custom event for same-tab updates
    const handleCartUpdate = () => updateCartCount();
    window.addEventListener('b2b-cart-update', handleCartUpdate);

    // Poll for changes every second (backup for same-tab updates)
    const interval = setInterval(updateCartCount, 1000);

    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('b2b-cart-update', handleCartUpdate);
      clearInterval(interval);
    };
  }, []);

  // Fetch portal info (public)
  const { data: portalInfo, isLoading: isLoadingPortal, error: portalError } = useQuery({
    queryKey: ['b2b-portal-info', orgSlug],
    queryFn: () => b2bService.getPortalInfo(orgSlug!),
    enabled: !!orgSlug,
  });

  // Fetch access status (only if authenticated)
  const { data: accessStatus, isLoading: isLoadingStatus } = useQuery({
    queryKey: ['b2b-access-status', orgSlug, user?.id],
    queryFn: () => b2bService.getAccessStatus(orgSlug!),
    enabled: !!orgSlug && isAuthenticated,
    retry: false, // Don't retry on auth errors
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await authService.logout();
    navigate(`/b2b/${orgSlug}`);
  };

  const isLoading = isLoadingPortal || (isAuthenticated && isLoadingStatus);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (portalError || !portalInfo) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          B2B Portal not found. Please check the URL or contact the organization.
        </Alert>
        <Button component={RouterLink} to="/" variant="contained">
          Go to Homepage
        </Button>
      </Container>
    );
  }

  // Determine what to show based on access status
  const hasApprovedAccess = accessStatus?.status === 'approved';
  const isPending = accessStatus?.status === 'pending';
  const isRejected = accessStatus?.status === 'rejected';
  const canRequestAccess = !isAuthenticated || accessStatus?.canRequestAccess;

  // Content to render based on status
  const renderContent = () => {
    // If not authenticated, show landing page for public routes
    if (!isAuthenticated) {
      // Allow access to landing, login, request-access pages
      const publicPaths = [`/b2b/${orgSlug}`, `/b2b/${orgSlug}/login`, `/b2b/${orgSlug}/request-access`];
      if (publicPaths.includes(location.pathname)) {
        return <Outlet context={{ portalInfo, accessStatus: null, isAuthenticated, user }} />;
      }
      // Redirect to login for other routes
      navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`);
      return null;
    }

    // If authenticated but has pending request
    if (isPending) {
      return <B2BPendingContent request={accessStatus?.request} portalInfo={portalInfo} />;
    }

    // If authenticated but rejected (and can't re-request)
    if (isRejected && !accessStatus?.canRequestAccess) {
      return (
        <B2BRejectedContent
          request={accessStatus?.request}
          portalInfo={portalInfo}
          banReason={accessStatus?.banReason}
          bannedUntil={accessStatus?.bannedUntil}
        />
      );
    }

    // If authenticated but rejected (can re-request) or no request yet
    if (!hasApprovedAccess && !isPending) {
      // Allow access to request-access page
      if (location.pathname === `/b2b/${orgSlug}/request-access`) {
        return (
          <Outlet
            context={{
              portalInfo,
              accessStatus,
              isAuthenticated,
              user,
              rejectedRequest: isRejected ? accessStatus?.request : null,
            }}
          />
        );
      }
      // Show request access prompt
      return (
        <B2BNoAccessContent
          portalInfo={portalInfo}
          orgSlug={orgSlug!}
          wasRejected={isRejected}
          reviewNotes={accessStatus?.request?.reviewNotes}
        />
      );
    }

    // Has approved access - show full B2B content
    return <Outlet context={{ portalInfo, accessStatus, isAuthenticated, user } as B2BPortalContext} />;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: palette.background }}>
      {/* Header */}
      <AppBar position="sticky" sx={{ bgcolor: palette.surface, borderBottom: `1px solid ${palette.border}` }}>
        <Toolbar sx={{ maxWidth: 1400, width: '100%', mx: 'auto' }}>
          {/* Logo / Org Name */}
          <Box
            component={RouterLink}
            to={`/b2b/${orgSlug}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: palette.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BusinessIcon sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color={palette.textPrimary}>
                {portalInfo.organization.name}
              </Typography>
              <Typography variant="caption" color={palette.textMuted} sx={{ display: 'block', mt: -0.5 }}>
                B2B Portal
              </Typography>
            </Box>
          </Box>

          {/* Navigation - only show if has approved access */}
          {isAuthenticated && hasApprovedAccess && (
            <Box sx={{ display: 'flex', gap: 1, ml: 4 }}>
              <Button
                component={RouterLink}
                to={`/b2b/${orgSlug}/products`}
                startIcon={<InventoryIcon />}
                sx={{ color: palette.textSecondary }}
              >
                Products
              </Button>
              <Button
                component={RouterLink}
                to={`/b2b/${orgSlug}/quotes`}
                startIcon={<RequestQuoteIcon />}
                sx={{ color: palette.textSecondary }}
              >
                My Quotes
              </Button>
              <Button
                component={RouterLink}
                to={`/b2b/${orgSlug}/reservations`}
                startIcon={<ShoppingBasketIcon />}
                sx={{ color: palette.textSecondary }}
              >
                My Reservations
              </Button>
              <Button
                component={RouterLink}
                to={`/b2b/${orgSlug}/invoices`}
                startIcon={<ReceiptIcon />}
                sx={{ color: palette.textSecondary }}
              >
                My Invoices
              </Button>
            </Box>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Right side */}
          {isAuthenticated ? (
            <>
              {hasApprovedAccess && (
                <Button
                  component={RouterLink}
                  to={`/b2b/${orgSlug}/cart`}
                  startIcon={
                    <Badge badgeContent={cartItemCount} color="error" max={99}>
                      <ShoppingCartIcon />
                    </Badge>
                  }
                  sx={{ mr: 2, color: palette.textSecondary }}
                >
                  Cart
                </Button>
              )}
              <IconButton onClick={handleMenuOpen} sx={{ color: palette.textPrimary }}>
                <AccountCircleIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email}
                  </Typography>
                </MenuItem>
                <Divider />
                {hasApprovedAccess && (
                  <MenuItem
                    component={RouterLink}
                    to={`/b2b/${orgSlug}/account`}
                    onClick={handleMenuClose}
                  >
                    <AccountCircleIcon sx={{ mr: 1, fontSize: 20 }} />
                    My Account
                  </MenuItem>
                )}
                <MenuItem component={RouterLink} to="/account/b2b-access" onClick={handleMenuClose}>
                  <BusinessIcon sx={{ mr: 1, fontSize: 20 }} />
                  My B2B Access
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                component={RouterLink}
                to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
                variant="outlined"
                sx={{ borderColor: palette.border }}
              >
                Login
              </Button>
              {canRequestAccess && portalInfo.settings.requestAccessEnabled && (
                <Button
                  component={RouterLink}
                  to={`/b2b/${orgSlug}/request-access`}
                  variant="contained"
                >
                  Request Access
                </Button>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {renderContent()}
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          mt: 'auto',
          py: 3,
          px: 2,
          bgcolor: palette.surface,
          borderTop: `1px solid ${palette.border}`,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {portalInfo.organization.name} B2B Portal
            </Typography>
            <Button
              component={RouterLink}
              to="/"
              startIcon={<StorefrontIcon />}
              size="small"
              sx={{ color: palette.textMuted }}
            >
              Visit Main Store
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

// Pending request content
interface PendingContentProps {
  request?: {
    createdAt: string;
    companyName: string;
  };
  portalInfo: PortalInfo;
}

const B2BPendingContent: React.FC<PendingContentProps> = ({ request, portalInfo }) => (
  <Box sx={{ textAlign: 'center', py: 8 }}>
    <HourglassEmptyIcon sx={{ fontSize: 80, color: 'warning.main', mb: 3 }} />
    <Typography variant="h4" fontWeight={600} gutterBottom>
      Access Request Pending
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
      Your request to access {portalInfo.organization.name}'s B2B portal is being reviewed.
      You'll receive a notification once your request has been processed.
    </Typography>
    {request && (
      <Box sx={{ bgcolor: 'grey.100', p: 3, borderRadius: 2, maxWidth: 400, mx: 'auto' }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Company:</strong> {request.companyName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          <strong>Submitted:</strong> {new Date(request.createdAt).toLocaleDateString()}
        </Typography>
      </Box>
    )}
  </Box>
);

// Rejected/Banned content
interface RejectedContentProps {
  request?: {
    reviewNotes?: string;
    reviewedAt?: string;
  };
  portalInfo: PortalInfo;
  banReason?: string;
  bannedUntil?: string;
}

const B2BRejectedContent: React.FC<RejectedContentProps> = ({
  request,
  portalInfo,
  banReason,
  bannedUntil,
}) => (
  <Box sx={{ textAlign: 'center', py: 8 }}>
    <BlockIcon sx={{ fontSize: 80, color: 'error.main', mb: 3 }} />
    <Typography variant="h4" fontWeight={600} gutterBottom>
      Access Denied
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
      {banReason
        ? `You have been blocked from requesting access to ${portalInfo.organization.name}'s B2B portal.`
        : `Your request to access ${portalInfo.organization.name}'s B2B portal was not approved.`}
    </Typography>
    {(request?.reviewNotes || banReason) && (
      <Box sx={{ bgcolor: 'error.50', p: 3, borderRadius: 2, maxWidth: 400, mx: 'auto', mb: 3 }}>
        <Typography variant="body2" color="error.dark">
          <strong>Reason:</strong> {banReason || request?.reviewNotes || 'No reason provided'}
        </Typography>
        {bannedUntil && (
          <Typography variant="body2" color="error.dark" sx={{ mt: 1 }}>
            <strong>You can request access again after:</strong>{' '}
            {new Date(bannedUntil).toLocaleDateString()}
          </Typography>
        )}
      </Box>
    )}
  </Box>
);

// No access yet content
interface NoAccessContentProps {
  portalInfo: PortalInfo;
  orgSlug: string;
  wasRejected?: boolean;
  reviewNotes?: string;
}

const B2BNoAccessContent: React.FC<NoAccessContentProps> = ({
  portalInfo,
  orgSlug,
  wasRejected,
  reviewNotes,
}) => (
  <Box sx={{ textAlign: 'center', py: 8 }}>
    <BusinessIcon sx={{ fontSize: 80, color: 'primary.main', mb: 3 }} />
    <Typography variant="h4" fontWeight={600} gutterBottom>
      {wasRejected ? 'Request Access Again' : 'B2B Access Required'}
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 500, mx: 'auto' }}>
      {wasRejected
        ? `Your previous request was not approved. You can submit a new request to access ${portalInfo.organization.name}'s B2B portal.`
        : `Request access to ${portalInfo.organization.name}'s B2B portal to view wholesale pricing and place orders.`}
    </Typography>
    {wasRejected && reviewNotes && (
      <Typography variant="body2" color="warning.dark" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
        Previous rejection reason: {reviewNotes}
      </Typography>
    )}
    {portalInfo.settings.requestAccessEnabled ? (
      <Button
        component={RouterLink}
        to={`/b2b/${orgSlug}/request-access`}
        variant="contained"
        size="large"
      >
        Request B2B Access
      </Button>
    ) : (
      <Typography variant="body2" color="text.secondary">
        Access requests are currently not being accepted. Please contact the organization directly.
      </Typography>
    )}
  </Box>
);
